import { useNavigate } from "react-router-dom";

import { Building, CreditCard, DashboardSquare } from "@/assets/icons";
import { Badge, Card } from "@/atoms";
import { BadgeStateEnum } from "@/enums";
import { AlfredAccountListDataInterface } from "@/interfaces";
import { formatCurrencyAmountHandler, generateValidClassNameHandler } from "@/lib/utils";
import { StatusType } from "@/types";
import { Button } from "@/ui/button";

const getStatusBadgeState = (status: StatusType) => {
    switch (status) {
    case "ACTIVE":
        return BadgeStateEnum.SUCCESS;
    case "INACTIVE":
        return BadgeStateEnum.LIGHT;
    default:
        return BadgeStateEnum.DEFAULT;
    }
};

export const AlfredAccountsListCard = ({
    className,
    data,
} : {
    className?: string,
    data: AlfredAccountListDataInterface,
}) => {
    const navigate = useNavigate();

    const handleViewAccount = (accountId: string) => {
        navigate(`/accounts/${accountId}`);
    };

    const handleShowTransactions = (accountId: string) => {
        navigate(`/ledger?account=${accountId}`);
    };

    const handleViewAllAccounts = () => {
        navigate("/accounts");
    };

    const headerContent = (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <DashboardSquare className="size-5 text-primary" />
                <span className="text-sm font-medium">Accounts Summary</span>
            </div>
            <div className="text-right">
                <div className="text-lg font-semibold text-primary">
                    {formatCurrencyAmountHandler(
                        data.totalBalance,
                        data.currency,
                    )}
                </div>
                <div className="text-xs text-gray-500">
                    {data.totalAccounts}
                    {" "}
                    accounts total
                </div>
            </div>
        </div>
    );

    const cardContent = (
        <>
            <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500">
                    Top 5 by Balance
                </div>
                {data.accounts.map((account) => {
                    const isAsset = account.category.toLowerCase().includes("asset");
                    const isInactive = account.status === "INACTIVE";

                    return (
                        <div
                            key={account.id}
                            className={generateValidClassNameHandler(
                                "flex flex-col gap-2 rounded-lg border bg-gray-50 p-2 sm:flex-row sm:items-center sm:justify-between",
                                isInactive && "opacity-60",
                            )}
                        >
                            {/* Name section - full width on mobile */}
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                                {isAsset ? (
                                    <Building className="size-4 shrink-0 text-primary" />
                                ) : (
                                    <CreditCard className="size-4 shrink-0 text-orange-500" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium">
                                        {/* Allow 2 lines on mobile, truncate on tablet+ */}
                                        <span className="line-clamp-2 sm:line-clamp-1">
                                            {account.name}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-500">
                                            {account.category}
                                        </span>
                                        <Badge
                                            className="ml-1 scale-75"
                                            state={getStatusBadgeState(account.status)}
                                        >
                                            {account.status}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            {/* Balance + Actions - row below name on mobile */}
                            <div className="flex items-center justify-between gap-2 sm:shrink-0">
                                <span
                                    className={generateValidClassNameHandler(
                                        "text-sm font-semibold",
                                        account.balance < 0 ? "text-red-600" : "text-gray-900",
                                    )}
                                >
                                    {formatCurrencyAmountHandler(
                                        account.balance,
                                        account.currency,
                                    )}
                                </span>
                                <div className="flex gap-1">
                                    <Button
                                        className="h-7 px-2 text-xs"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleViewAccount(account.id)}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        className="h-7 px-2 text-xs"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleShowTransactions(account.id)}
                                    >
                                        Txns
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {data.accounts.length === 0 && (
                    <div className="py-4 text-center text-sm text-gray-500">
                        No accounts found
                    </div>
                )}
            </div>

            {/* Action button as part of content (Option 3) */}
            <div className="mt-3 flex justify-center border-t pt-3">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={handleViewAllAccounts}
                >
                    View All
                    {" "}
                    {data.totalAccounts}
                    {" "}
                    Accounts
                </Button>
            </div>
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-3 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full border shadow-sm",
                className,
            )}
        />
    );
};
